import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  BatteryWarning, BellRing, CalendarCheck, ChevronRight, FlaskConical, Mail, MoonStar, RefreshCw, ShieldCheck, X,
} from 'lucide-react';

import { loadMobileView } from './api';
import { Empty, Failed, Head, Loading, Pill, SearchField } from './parts';
import { tx } from './i18n';
import { useLiveResource } from './useLive';
import { LockScreen } from './AppPhone';
import {
  CHART, COMPACT, ChartTip, KpiRow, NUMBER, Panel, PlatformMark, PushCard, RangeTabs, Share, ago, averageOf, counted, emailReasonLabel,
  hourly, initials, percent, pointLabel, pushTypeLabel, span, tipPoint, useNow, versusAverage,
} from './MobileParts';

const TYPE_ICONS = {
  sync_request: RefreshCw, no_habits_today: CalendarCheck, low_battery: BatteryWarning, test_notification: FlaskConical, non_sync: MoonStar,
};
const RECIPIENTS_SHOWN = 200;

function Kinds({ types, diagnostics, onOpen }) {
  if (types.length === 0) return <Empty title={tx('ui.mobile.no_pushes')} />;
  const most = Math.max(1, ...types.map((entry) => entry.sent));
  return (
    <ol className="xo-mob-kinds">
      {types.map((entry) => {
        const Icon = TYPE_ICONS[entry.type] || BellRing;
        return (
          <li key={entry.type}>
            <button type="button" onClick={() => onOpen(entry.type)} title={tx('ui.mobile.see_recipients')}>
              <span className="xo-mob-rank-icon"><Icon aria-hidden="true" /></span>
              <span className="xo-mob-rank-main">
                <strong>{pushTypeLabel(entry.type)}</strong>
                <small>{entry.silent > 0 ? tx('ui.mobile.silent_nudge') : tx('ui.mobile.visible_reminder')}
                  {' · '}{counted('ui.mobile.people_count', entry.recipients.length)}
                  {diagnostics && ` · ${tx('ui.mobile.type_diag', { failed: NUMBER.format(entry.failed), latency: span(entry.medianLatencyMs) })}`}</small>
              </span>
              <span className="xo-mob-kind-platforms">
                <span><PlatformMark platform="ios" />{NUMBER.format(entry.ios)}</span>
                <span><PlatformMark platform="android" />{NUMBER.format(entry.android)}</span>
              </span>
              <Share value={entry.sent} total={most} />
              <em>{NUMBER.format(entry.sent)}</em>
              <ChevronRight aria-hidden="true" />
            </button>
          </li>
        );
      })}
    </ol>
  );
}

// Who one kind of push went to in the range: each person once, most recent first,
// with how many they got. Admin+ also see the ones that never arrived.
function Recipients({ kind, days, diagnostics, now, onClose }) {
  const [search, setSearch] = useState('');
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  const Icon = TYPE_ICONS[kind.type] || BellRing;
  const term = search.trim().toLowerCase();
  const people = kind.recipients.filter((person) => term === '' || [person.name, person.email].some((value) => typeof value === 'string' && value.toLowerCase().includes(term)));
  const platforms = ['ios', 'android'].map((platform) => [platform, kind.recipients.filter((person) => person.platform === platform).length]);
  return (
    <div className="xo-mob-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.div className="xo-mob-dialog is-wide xo-mob-recipients" role="dialog" aria-modal="true" aria-labelledby="xo-mob-recipients-title"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
        <header>
          <span className="xo-mob-dialog-icon"><Icon aria-hidden="true" /></span>
          <div>
            <h2 id="xo-mob-recipients-title">{pushTypeLabel(kind.type)}</h2>
            <p>{counted('ui.mobile.recipients_detail', kind.recipients.length, { days: NUMBER.format(days) })}</p>
          </div>
          <button type="button" className="xo-mob-icon-button" aria-label={tx('ui.mobile.close')} onClick={onClose}><X /></button>
        </header>
        <div className="xo-mob-recipient-facts">
          <span><small>{tx('ui.mobile.delivered')}</small><strong>{NUMBER.format(kind.sent)}</strong></span>
          <span><small>{tx('ui.mobile.people')}</small><strong>{NUMBER.format(kind.recipients.length)}</strong></span>
          {platforms.map(([platform, count]) => (
            <span key={platform}><small><PlatformMark platform={platform} withLabel /></small><strong>{NUMBER.format(count)}</strong></span>
          ))}
        </div>
        {kind.recipients.length > 6 && (
          <SearchField value={search} onChange={setSearch} placeholder={tx('ui.mobile.search_people')} autoComplete="off" />
        )}
        {people.length === 0 ? <Empty title={tx('ui.mobile.no_recipients_match')} /> : (
          <ol className="xo-mob-recipient-list">
            {people.map((person, index) => {
              const body = (
                <>
                  <span className="xo-mob-avatar is-small">{initials(person.name, person.email)}</span>
                  <span className="xo-mob-recipient-main">
                    <strong>{person.name || person.email || tx('ui.mobile.deleted_account')}</strong>
                    <small>{person.name ? person.email : ''}</small>
                  </span>
                  <PlatformMark platform={person.platform} />
                  <span className="xo-mob-recipient-count">
                    <strong>{counted('ui.mobile.times_count', person.sent)}</strong>
                    <small>{tx('ui.mobile.last_sent', { when: ago(person.lastAt, now) })}</small>
                  </span>
                  {diagnostics && person.failed > 0 ? <small className="is-bad">{counted('ui.mobile.not_arrived_count', person.failed)}</small> : <i />}
                  {person.accountUuid ? <ChevronRight aria-hidden="true" /> : <i />}
                </>
              );
              return (
                <li key={person.accountUuid || `gone-${index}`}>
                  {person.accountUuid ? <Link to={`/xomics/mobile/accounts?account=${person.accountUuid}`}>{body}</Link> : <span>{body}</span>}
                </li>
              );
            })}
          </ol>
        )}
        {kind.recipients.length >= RECIPIENTS_SHOWN && <p className="xo-mob-footnote">{tx('ui.mobile.recipients_cap', { count: NUMBER.format(RECIPIENTS_SHOWN) })}</p>}
      </motion.div>
    </div>
  );
}

function HourBars({ hours }) {
  const max = Math.max(1, ...hours);
  if (hours.every((value) => value === 0)) return <Empty title={tx('ui.mobile.no_pushes')} />;
  return (
    <div className="xo-mob-hour-bars">
      {hours.map((value, hour) => (
        <span key={hour} title={tx('ui.mobile.hour_value', { hour: String(hour).padStart(2, '0'), value })}>
          <i style={{ height: `${(value / max) * 100}%` }} />
          <small>{hour % 3 === 0 ? String(hour).padStart(2, '0') : ''}</small>
        </span>
      ))}
    </div>
  );
}

// Admin+ only, and folded away: what did not arrive, and why.
function Diagnostics({ data, now }) {
  const outcomes = data.push.outcomes;
  const attempts = Object.values(outcomes).reduce((sum, value) => sum + value, 0);
  const failedPushes = data.push.recent.filter((push) => push.outcome !== 'sent');
  const failedEmails = data.email.recent.filter((email) => email.status !== 'sent');
  return (
    <details className="xo-mob-diagnostics">
      <summary>
        <ShieldCheck aria-hidden="true" />
        <span><strong>{tx('ui.mobile.delivery_diagnostics')}</strong><small>{tx('ui.mobile.delivery_diagnostics_detail')}</small></span>
        <Pill tone="flat">{tx('ui.mobile.admin_plus_only')}</Pill>
      </summary>
      <div className="xo-mob-diagnostics-body">
        <div className="xo-mob-diag-facts">
          <span><small>{tx('ui.mobile.delivery_rate')}</small><strong>{percent(outcomes.sent, attempts)}</strong><em>{counted('ui.mobile.of_attempts', attempts)}</em></span>
          <span><small>{tx('ui.mobile.outcome_failed')}</small><strong>{NUMBER.format(outcomes.failed)}</strong><em>{counted('ui.mobile.tokens_removed', outcomes.token_deleted)}</em></span>
          <span><small>{tx('ui.mobile.outcome_no_tokens')}</small><strong>{NUMBER.format(outcomes.no_tokens)}</strong><em>{tx('ui.mobile.no_tokens_detail')}</em></span>
        </div>
        <div className="xo-mob-row is-halves">
          <Panel title={tx('ui.mobile.failed_pushes')}>
            {failedPushes.length === 0 ? <Empty title={tx('ui.mobile.no_failed_pushes')} /> : (
              <ol className="xo-mob-errors">
                {failedPushes.map((push, index) => (
                  <li key={`${push.at}-${index}`}><code>{push.error || push.outcome}</code><span>{pushTypeLabel(push.type)} · {ago(push.at, now)}</span></li>
                ))}
              </ol>
            )}
          </Panel>
          <Panel title={tx('ui.mobile.failed_emails')}>
            {failedEmails.length === 0 ? <Empty title={tx('ui.mobile.no_failed_emails')} /> : (
              <ol className="xo-mob-errors">
                {failedEmails.map((email, index) => (
                  <li key={`${email.at}-${index}`}><code>{email.failure || email.status}</code><span>{emailReasonLabel(email.reason)} · {email.destination} · {ago(email.at, now)}</span></li>
                ))}
              </ol>
            )}
          </Panel>
        </div>
      </div>
    </details>
  );
}

export default function MobileNotifications() {
  const [days, setDays] = useState(30);
  const load = useCallback(() => loadMobileView('notifications', days), [days]);
  const report = useLiveResource([], load, { reconcileMs: 60000 });
  const now = useNow(30000);
  const [openType, setOpenType] = useState(null);
  const [kindsPanel, setKindsPanel] = useState(null);
  const [phoneScale, setPhoneScale] = useState(0.6);
  const data = report.data;

  // The lock screen stands as tall as the kinds of push beside it: the phone is
  // scaled to that panel's height, less the lock panel's own heading and padding.
  useEffect(() => {
    if (!kindsPanel || typeof ResizeObserver === 'undefined') return undefined;
    const fit = () => setPhoneScale(Math.max(0.4, Math.min(0.7, (kindsPanel.getBoundingClientRect().height - 106) / 868)));
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(kindsPanel);
    return () => observer.disconnect();
  }, [kindsPanel]);

  const derived = useMemo(() => {
    if (!data) return null;
    const delivered = data.diagnostics ? data.push.recent.filter((push) => push.outcome === 'sent') : data.push.recent;
    const firstDay = data.push.series.findIndex((point) => point.sent > 0);
    const series = data.push.series.slice(firstDay < 0 ? 0 : Math.max(0, Math.min(firstDay, data.push.series.length - 7)));
    return {
      delivered,
      shown: delivered.filter((push) => !push.silent),
      silent: delivered.filter((push) => push.silent),
      series,
      sentTotal: series.reduce((sum, point) => sum + point.sent, 0),
      sentAverage: averageOf(series.map((point) => point.sent)),
      reminders: data.push.byType.filter((entry) => entry.silent === 0).reduce((sum, entry) => sum + entry.sent, 0),
      nudges: data.push.byType.filter((entry) => entry.silent > 0).reduce((sum, entry) => sum + entry.sent, 0),
    };
  }, [data]);

  if (report.loading && data === null) return <Loading label={tx('ui.mobile.reading_notifications')} />;
  if (report.error && data === null) return <Failed message={report.error} onRetry={report.refresh} />;
  const { shown, silent, series, reminders, nudges, sentTotal, sentAverage } = derived;
  const openKind = openType === null ? null : data.push.byType.find((entry) => entry.type === openType) || null;
  const tokens = (platform) => data.tokens.filter((row) => row.platform === platform).reduce((sum, row) => sum + row.accounts, 0);
  const emailsSent = data.email.byReason.reduce((sum, row) => sum + row.sent, 0);
  const emailMost = Math.max(1, ...data.email.byReason.map((row) => row.sent));
  const lock = shown.slice(0, 3).map((push, index) => ({
    id: `${push.at}-${index}`, title: push.title || pushTypeLabel(push.type), body: push.body, when: ago(push.at, now),
  }));

  return (
    <div className="xo-platform-page xo-mobile-page">
      <Head title={tx('ui.mobile.notifications_title')} subtitle={tx('ui.mobile.notifications_subtitle')}>
        <RangeTabs value={days} onChange={setDays} />
      </Head>
      {report.error && <p className="xo-alert t-warn" role="alert">{report.error}</p>}

      <KpiRow items={[
        { label: tx('ui.mobile.pushes_delivered'), value: NUMBER.format(reminders + nudges), detail: `${counted('ui.mobile.reminders_count', reminders)} · ${counted('ui.mobile.nudges_count', nudges)}`, trend: data.push.series.map((point) => point.sent) },
        { label: tx('ui.mobile.push_ready_people'), value: NUMBER.format(tokens('ios') + tokens('android')), detail: tx('ui.mobile.push_ready_split', { ios: NUMBER.format(tokens('ios')), android: NUMBER.format(tokens('android')) }) },
        { label: tx('ui.mobile.emails_sent'), value: NUMBER.format(emailsSent), detail: counted('ui.mobile.email_reasons_count', data.email.byReason.length) },
        { label: tx('ui.mobile.busiest_hour'), value: data.push.localHours.some((value) => value > 0) ? `${String(data.push.localHours.indexOf(Math.max(...data.push.localHours))).padStart(2, '0')}:00` : '—', detail: tx('ui.mobile.busiest_hour_push_detail') },
      ]} />

      <div className="xo-mob-row is-phone-left">
        <Panel title={tx('ui.mobile.lock_title')} detail={tx('ui.mobile.lock_detail')} className="is-phone-panel">
          {lock.length === 0 ? <Empty title={tx('ui.mobile.no_pushes')} /> : (
            <LockScreen notifications={lock} at={shown[0].at} platform={shown[0].platform === 'android' ? 'android' : 'ios'} appName={tx('ui.mobile.app_name')} scale={phoneScale} />
          )}
        </Panel>
        <div ref={setKindsPanel} className="xo-mob-measured">
        <Panel title={tx('ui.mobile.push_types_title')} detail={tx('ui.mobile.push_types_detail')}>
          <Kinds types={data.push.byType} diagnostics={data.diagnostics} onOpen={setOpenType} />
          <div className="xo-mob-row is-halves is-inner">
            <div>
              <h3 className="xo-mob-subhead">{tx(hourly(data.step) ? 'ui.mobile.delivered_hourly' : 'ui.mobile.delivered_daily')}</h3>
              <div className="xo-mob-chart is-short">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={series} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={CHART.grid} />
                    <XAxis dataKey="at" tickFormatter={pointLabel(data.step)} tickLine={false} axisLine={false} minTickGap={20} tick={CHART.tick} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={36} tickFormatter={(value) => COMPACT.format(value)} />
                    <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
                      <ChartTip title={tipPoint(data.step)}
                        rows={(point) => [
                          { name: tx('ui.mobile.delivered'), color: CHART.ink, text: NUMBER.format(point.sent) },
                          data.diagnostics && point.failed > 0
                            ? { name: tx('ui.mobile.outcome_failed'), color: CHART.failed, text: NUMBER.format(point.failed) } : null,
                        ]}
                        facts={(point) => [
                          { label: tx('ui.mobile.tip_share_of_range'), value: percent(point.sent, sentTotal) },
                          versusAverage(point.sent, sentAverage),
                        ]} />
                    )} />
                    <Bar dataKey="sent" fill={CHART.ink} name={tx('ui.mobile.delivered')} radius={[3, 3, 0, 0]} maxBarSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <h3 className="xo-mob-subhead">{tx('ui.mobile.land_title')}</h3>
              <HourBars hours={data.push.localHours} />
            </div>
          </div>
        </Panel>
        </div>
      </div>

      <div className="xo-mob-row is-halves is-even">
        <Panel title={tx('ui.mobile.recent_pushes')} detail={tx('ui.mobile.recent_pushes_detail')} className="is-pushes">
          {silent.length > 0 && (
            <p className="xo-mob-quiet-line"><RefreshCw aria-hidden="true" />{counted('ui.mobile.silent_count', silent.length, { when: ago(silent[0].at, now) })}</p>
          )}
          {shown.length === 0 ? <Empty title={tx('ui.mobile.no_pushes')} /> : (
            <div className="xo-mob-push-list">
              {shown.slice(0, 20).map((push, index) => (
                <PushCard key={`${push.at}-${index}`} push={data.diagnostics ? { ...push, outcome: undefined, latencyMs: undefined } : push} now={now}
                  to={push.accountUuid ? `/xomics/mobile/accounts?account=${push.accountUuid}` : null} />
              ))}
            </div>
          )}
        </Panel>
        <Panel title={tx('ui.mobile.emails_title')} detail={tx('ui.mobile.emails_detail')}>
          {data.email.byReason.length === 0 ? <Empty title={tx('ui.mobile.no_emails')} /> : (
            <ol className="xo-mob-rank is-tight">
              {data.email.byReason.map((row) => (
                <li key={row.reason}>
                  <span className="xo-mob-rank-icon"><Mail aria-hidden="true" /></span>
                  <span className="xo-mob-rank-main"><strong>{emailReasonLabel(row.reason)}</strong></span>
                  <Share value={row.sent} total={emailMost} />
                  <em>{NUMBER.format(row.sent)}</em>
                </li>
              ))}
            </ol>
          )}
          <h3 className="xo-mob-subhead">{tx('ui.mobile.latest_emails')}</h3>
          <ol className="xo-mob-mails">
            {data.email.recent.filter((email) => email.status === 'sent').slice(0, 10).map((email, index) => (
              <li key={`${email.at}-${index}`}>
                <span><strong>{email.subject || emailReasonLabel(email.reason)}</strong><small>{emailReasonLabel(email.reason)} · {email.destination}</small></span>
                <em>{ago(email.at, now)}</em>
              </li>
            ))}
          </ol>
        </Panel>
      </div>

      {data.diagnostics && data.push.outcomes && <Diagnostics data={data} now={now} />}
      <p className="xo-mob-footnote">{counted('ui.mobile.pushes_total_line', data.push.total, {}, COMPACT)}</p>

      <AnimatePresence>
        {openKind && <Recipients key={openKind.type} kind={openKind} days={days} diagnostics={data.diagnostics} now={now} onClose={() => setOpenType(null)} />}
      </AnimatePresence>
    </div>
  );
}
